// The following import still happens, but is dynamically imported in the getProducer function
// import { EventHubProducerClient } from '@azure/event-hubs'
import * as R from 'ramda'
import { v4 as uuidv4 } from 'uuid'

import { dupCheck } from '../../../src/utils/funcs.js'

const selectEventPropsToLog = R.pick(['_eventId', '_eventType', 'timestamp', 'installId'])

const CHECK_FOR_DUPES = false // set to true to check for duplicates in the batch before sending

export const getCurrentTimestamp = () => new Date().toISOString()

const getCurrentAnalyticsEnv = () =>
  location.host.includes('apps.locuslabs') || location.host.includes('localhost')
    ? 'test'
    : 'prod'

export async function initEventManager ({ webConfigId, accountId, config, log }) {
  const options = {
    oneEventPerBatch: false,
    maxBatchAge: 10 * 1000, // 10 seconds
    credentialsRefreshInterval: 30 * 60 * 1000 // 30 minutes
  }

  const noVenueIdEvents = ['appAndUserProperties', 'sessionEnd']
  const noUserPositionEvents = ['appAndUserProperties', 'sessionEnd', 'venueLoad']

  const state = {
    venueId: null,
    userPosition: null
  }

  let producer = null
  let batch = null
  let batchCreateTimestamp = Date.now()
  let batchedEvents = []

  const getProducer = async () => {
    if (producer)
      await producer.close() // close previously opened producer
    const env = getCurrentAnalyticsEnv()
    const hubsInfoEndpoint = `https://rest.locuslabs.com/v3/azureServicebusCredentials/${accountId}?environmentName=${env}&platform=webengine`

    const { EventHubProducerClient } = await import('@azure/event-hubs')
    return fetch(hubsInfoEndpoint)
      .then(res => res.json()) // https://gitlab.com/groups/locuslabs/analytics/-/wikis/Client%20side%20storage%20and%20sending%20events#https
      .then(eventHubsInfo => new EventHubProducerClient(eventHubsInfo.connectionString, eventHubsInfo.eventHubName))
      .catch(e => {
        log.error(e)
        producer = null // de-reference these so we are clear...
        batch = null
        if (batchedEvents.length > 0) log.error(`Batch was dropped before sending events (${batchedEvents.length} events)`)
        batchedEvents = []
      })
  }

  async function newBatch () {
    batchCreateTimestamp = Date.now()
    batch = await producer.createBatch()
    batchedEvents = []
  }

  async function sendBatch () {
    if (producer) {
      await producer.sendBatch(batch)
      batchedEvents.forEach(event => log.info('EventLifecycle [sent]:', event._eventId))
      if (CHECK_FOR_DUPES)
        dupCheck(batchedEvents)
      log.info(`Sent batch of ${batch.count} events to Azure`)
      newBatch()
    } else log.warn('Attempted to send a batch but producer was null!')
  }

  async function refreshCredentials () {
    if (batch && batch.count)
      await sendBatch()
    if (producer)
      await producer.close()
    producer = await getProducer()
    newBatch() // now that we have a new producer, we need a new batch connected to it.
  }

  const getInstallId = () => {
    const { installId } = JSON.parse(localStorage.getItem(`ll_installId_${webConfigId}`)) || {}
    if (!installId) {
      const newInstallId = uuidv4()
      localStorage.setItem(`ll_installId_${webConfigId}`, JSON.stringify({ installId: newInstallId }))
      return newInstallId
    }
    return installId
  }

  const removeCachedUserPosition = () => delete state.userPosition

  const initEventQueue = [] // used to queue up events until we have a producer and batch

  const submitEvent = async event => {
    if (event._eventType === 'venueLoad') {
      state.venueId = event.venueId
    }
    if (event._eventType === 'userPosition') {
      state.userPosition = { ...event.userPosition }
    }
    event._eventId = uuidv4()
    event._eventVersion = event._eventVersion || '1'
    event.timestamp = event.timestamp || getCurrentTimestamp()
    if (noVenueIdEvents.indexOf(event._eventType) === -1 && state.venueId) {
      event.venueId = state.venueId
    }
    if (noUserPositionEvents.indexOf(event._eventType) === -1 && state.userPosition) {
      event.userPosition = { ...state.userPosition }
    }
    event.installId = getInstallId()
    log.info('submitEvent', event)
    log.info(`EventLifecycle [tracked]: ${JSON.stringify(selectEventPropsToLog(event))}`)

    if (!config.disableSending) {
      if (producer && batch)
        sendEvent(event)
      else {
        // producer is not initialized yet, so lets queue up the event until we have a producer (up to a max of 20)
        if (initEventQueue.length <= 20)
          initEventQueue.push(event)
      }
    }
  }

  async function sendEvent (event) {
    const isAdded = batch.tryAdd({
      body: event,
      properties: {
        MessageType: event._eventType,
        SourceMessageVersion: '1',
        ServiceType: 'locuslabs',
        AccountId: accountId
      }
    })
    if (isAdded) {
      batchedEvents.push(event)
      if (options.oneEventPerBatch)
        await sendBatch()
      else
        if (batch.count === 1)
          batchCreateTimestamp = Date.now() // reset batch start on first actual event
    } else {
      if (batch.count === 0) {
        // If we can't add it and the batch is empty that means the message we're trying to send
        // is too large, even when it would be the _only_ message in the batch.
        //
        // At this point you'll need to decide if you're okay with skipping this message entirely
        // or find some way to shrink it.
        log.error(`EventLifecycle [skipped]: [${event._eventId}] was too large (${JSON.stringify(event).length})`)
      } else {
        // otherwise this just signals a good spot to send our batch
        log.info('Batch is full...sending')
        await sendBatch()
        // and re-try adding this event to the new batch
        return sendEvent(event)
      }
    }
  }

  function checkBatchAge () {
    if (batch) {
      log.info(`checking batch with count ${batch.count} and age ${Date.now() - batchCreateTimestamp}`)
      if (batch.count > 0 && (Date.now() - batchCreateTimestamp) > options.maxBatchAge)
        sendBatch()
    }
  }

  async function initEventHubs () {
    if (!config.disableSending) {
      producer = await getProducer()
      await newBatch()
      log.info('EventHubs producer created - ready to batch and send events')
      if (initEventQueue.length > 0) {
        initEventQueue.forEach(event => sendEvent(event))
        initEventQueue.length = 0 // clear the queue
      }

      const checkBatchAgeIntervalId = setInterval(checkBatchAge, options.maxBatchAge)
      const refreshCredentialsIntervalId = setInterval(refreshCredentials, options.credentialsRefreshInterval)

      if (typeof checkBatchAgeIntervalId.unref === 'function')
        checkBatchAgeIntervalId.unref() // Node only - do not keep-alive Lambda or other short-term environments

      if (typeof refreshCredentialsIntervalId.unref === 'function')
        refreshCredentialsIntervalId.unref() // Node only - do not keep-alive Lambda or other short-term environments
    }
  }

  setTimeout(initEventHubs, 5000) // kick off the producer creation and batch creation asynchronously after a short delay

  return {
    getInstallId,
    submitEvent,
    removeCachedUserPosition
  }
}
